<template>
  <el-dialog
    :visible.sync="visible"
    width="400px"
    :close-on-click-modal="false"
    :modal="true"
    :lock-scroll="true"
    custom-class="auth-dialog"
    @close="$emit('close')"
  >
    <div class="signup-flow" @mousedown="onModalMouseDown" @click="onModalClick">
      <!-- Single Page Signup -->
      <div class="auth-form">
        <!-- Subscription Summary -->
        <div class="subscription-summary">
          <div class="plan-details">
            <h4>InterviewSignal Pro</h4>
            <div class="pricing-offer">
              <span class="price-amount">$20/month</span>
              <span class="feature-list">Live Interview Help • AI Interview Practice • Latest AI</span>
            </div>
          </div>
        </div>

        <!-- Account Information -->
        <div class="form-group compact">
          <el-input 
            v-model="signupForm.fullName" 
            placeholder="Full Name *"
            class="auth-input"
            size="small"
            :disabled="loading"
          />
        </div>
        
        <div class="form-group compact">
          <el-input 
            v-model="signupForm.email" 
            type="email" 
            placeholder="Email Address *"
            class="auth-input"
            size="small"
            :disabled="loading"
          />
        </div>

        <div class="form-group compact">
          <el-input 
            v-model="signupForm.password" 
            type="password" 
            placeholder="Password * (min 6 characters)"
            class="auth-input"
            size="small"
            :disabled="loading"
            show-password
          />
        </div>

        <!-- Payment Section -->
        <div class="payment-section compact">
          <label class="section-label">Payment Information</label>
          <div id="stripe-card-element" class="stripe-element compact">
            <div class="stripe-loading">Loading payment form...</div>
          </div>
          <div id="stripe-card-errors" class="stripe-errors"></div>
        </div>

        <!-- Coupon Section -->
        <div class="coupon-section">
          <div class="coupon-toggle">
            <el-checkbox v-model="showCouponField" @change="toggleCouponField" size="small">
              Have a coupon code?
            </el-checkbox>
          </div>
          <div v-if="showCouponField" class="coupon-input">
            <el-input 
              v-model="signupForm.couponCode" 
              placeholder="Enter coupon code"
              class="auth-input coupon-field"
              size="small"
              :disabled="loading"
              @blur="validateCoupon"
            />
            <div v-if="couponStatus.message" 
                 :class="['coupon-status', couponStatus.type]">
              {{ couponStatus.message }}
            </div>
          </div>
        </div>

        <!-- Compact Money-Back Guarantee -->
        <div class="compact-guarantee">
          <i class="el-icon-success"></i>
          <span>30-Day Money-Back Guarantee</span>
        </div>

        <!-- Terms Agreement -->
        <div class="terms-agreement">
          <p>By clicking "Subscribe", you agree to our 
            <a href="/legal/terms-of-service.html" target="_blank" class="terms-link">Terms of Service</a>, 
            <a href="/legal/privacy-policy.html" target="_blank" class="terms-link">Privacy Policy</a>, and 
            <a href="/legal/cookie-policy.html" target="_blank" class="terms-link">Cookie Policy</a>.
          </p>
        </div>

        <!-- Subscribe Button -->
        <el-button 
          type="primary" 
          @click="handleSubscribe" 
          :loading="loading"
          :disabled="loading || !canProceed"
          class="subscribe-button"
        >
          Subscribe Now
        </el-button>
      </div>

      <!-- Status Messages -->
      <div v-if="statusMessage" class="status-message" :class="statusMessage.type">
        <i :class="statusMessage.type === 'success' ? 'el-icon-check' : 'el-icon-warning'"></i>
        <div class="message-content">
          <div class="message-title">{{ statusMessage.title }}</div>
          <div class="message-text">{{ statusMessage.text }}</div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'SignupWithSubscriptionModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      statusMessage: null,
      stripe: null,
      cardElement: null,
      showCouponField: false,
      couponStatus: { message: '', type: '' },
      mouseDownInsideModal: false,
      signupForm: {
        fullName: '',
        email: '',
        password: '',
        couponCode: ''
      }
    }
  },
  computed: {
    canProceed() {
      return this.signupForm.fullName && 
             this.signupForm.email &&
             this.signupForm.password &&
             this.signupForm.password.length >= 6 &&
             this.isValidEmail(this.signupForm.email)
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        this.$nextTick(() => {
          this.initializeStripe()
          // Attach click listener when modal opens
          setTimeout(() => {
            const modalWrapper = this.$el?.closest('.el-dialog__wrapper')
            if (modalWrapper) {
              modalWrapper.addEventListener('click', this.onWrapperClick)
            }
          }, 200)
        })
        // Prevent body scroll when modal opens
        document.body.style.overflow = 'hidden'
      } else {
        // Remove click listener when modal closes
        const modalWrapper = this.$el?.closest('.el-dialog__wrapper')
        if (modalWrapper) {
          modalWrapper.removeEventListener('click', this.onWrapperClick)
        }
        // Restore body scroll when modal closes
        document.body.style.overflow = ''
      }
    }
  },
  mounted() {
    // Add click listener to modal wrapper to detect outside clicks
    this.$nextTick(() => {
      setTimeout(() => {
        const modalWrapper = this.$el.closest('.el-dialog__wrapper')
        if (modalWrapper) {
          modalWrapper.addEventListener('click', this.onWrapperClick)
        }
      }, 100)
    })
  },
  beforeDestroy() {
    // Clean up event listener
    const modalWrapper = this.$el?.closest('.el-dialog__wrapper')
    if (modalWrapper) {
      modalWrapper.removeEventListener('click', this.onWrapperClick)
    }
    // Ensure body scroll is restored when component is destroyed
    document.body.style.overflow = ''
  },
  methods: {
    onModalMouseDown() {
      this.mouseDownInsideModal = true
    },
    
    onModalClick(event) {
      // Prevent click from bubbling up to wrapper
      event.stopPropagation()
    },
    
    onWrapperClick(event) {
      // Only close if the mousedown didn't start inside the modal
      if (!this.mouseDownInsideModal && event.target.classList.contains('el-dialog__wrapper')) {
        this.$emit('close')
      }
      // Reset the flag after each click
      this.mouseDownInsideModal = false
    },
    resetForm() {
      this.loading = false
      this.statusMessage = null
      this.showCouponField = false
      this.couponStatus = { message: '', type: '' }
      this.signupForm = {
        fullName: '',
        email: '',
        password: '',
        couponCode: ''
      }
    },

    async initializeStripe() {
      try {
        // Try multiple sources for the Stripe key
        const stripeKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || 
                         'pk_test_51KowQwKK3t54Z5JeggcWXjBNQ9S5GnPLAEKztSkAPHpmMihijc8AAkfkQhU4sPKvDxezJ41XeNJFIDeuKuUqJUnY00HkJODHyw'
        
        console.log('🔑 Initializing Stripe with key:', stripeKey?.substring(0, 20) + '...')
        
        if (!stripeKey) {
          throw new Error('Stripe publishable key not found in environment variables')
        }
        
        // Check if Stripe is already loaded
        if (window.Stripe) {
          this.stripe = window.Stripe(stripeKey)
          console.log('✅ Stripe initialized from existing window.Stripe')
          this.setupCardElement()
        } else {
          // Show loading state while Stripe loads
          const cardContainer = document.getElementById('stripe-card-element')
          if (cardContainer) {
            cardContainer.innerHTML = '<div style="padding: 12px; color: #666; font-style: italic;">Loading payment form...</div>'
          }
          
          console.log('📦 Loading Stripe script...')
          // Load Stripe script
          const script = document.createElement('script')
          script.src = 'https://js.stripe.com/v3/'
          script.onload = () => {
            console.log('✅ Stripe script loaded')
            this.stripe = window.Stripe(stripeKey)
            this.setupCardElement()
          }
          script.onerror = () => {
            console.error('❌ Failed to load Stripe script')
            this.showStatusMessage('error', 'Payment Error', 'Failed to load Stripe. Please check your internet connection.')
          }
          document.head.appendChild(script)
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize Stripe:', error)
        this.showStatusMessage('error', 'Payment Error', 'Failed to load payment processor: ' + error.message)
      }
    },
    
    setupCardElement() {
      if (!this.stripe) {
        console.error('❌ Stripe not initialized')
        return
      }
      
      console.log('🎨 Setting up card element...')
      const cardContainer = document.getElementById('stripe-card-element')
      if (!cardContainer) {
        console.error('❌ Card element container not found')
        this.showStatusMessage('error', 'Payment Error', 'Payment form container not found.')
        return
      }
      
      // Clear loading message
      cardContainer.innerHTML = ''
      
      const elements = this.stripe.elements()
      // Updated Stripe Elements with better visibility
      this.cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'normal',
            lineHeight: '24px',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#9e2146',
          }
        }
      })
      
      // Mount the card element immediately
      try {
        this.cardElement.mount('#stripe-card-element')
        console.log('✅ Card element mounted successfully')
        
        // Verify mounting success quickly
        setTimeout(() => {
          const iframe = cardContainer.querySelector('iframe')
          if (iframe) {
            console.log('✅ Stripe iframe found and loaded')
          } else {
            console.warn('⚠️ Stripe iframe not found after mounting')
          }
        }, 500)
        
      } catch (error) {
        console.error('❌ Failed to mount card element:', error)
        this.showStatusMessage('error', 'Payment Error', 'Failed to initialize payment form: ' + error.message)
      }
      
      this.cardElement.on('change', (event) => {
        const displayError = document.getElementById('stripe-card-errors')
        if (event.error) {
          displayError.textContent = event.error.message
        } else {
          displayError.textContent = ''
        }
      })
    },
    
    toggleCouponField() {
      if (!this.showCouponField) {
        this.signupForm.couponCode = ''
        this.couponStatus = { message: '', type: '' }
      }
    },
    
    async validateCoupon() {
      if (!this.signupForm.couponCode.trim()) {
        this.couponStatus = { message: '', type: '' }
        return
      }
      
      try {
        const apiUrl = process.env.NODE_ENV === 'production' 
          ? 'https://interview-signal-api-production.up.railway.app'
          : '/api'
        
        const response = await fetch(`${apiUrl}/api/validate-coupon`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            coupon_code: this.signupForm.couponCode
          })
        })
        
        const result = await response.json()
        
        if (result.valid) {
          this.couponStatus = {
            message: `✅ ${result.message}`,
            type: 'success'
          }
        } else {
          this.couponStatus = {
            message: result.message || 'Invalid coupon code',
            type: 'error'
          }
        }
      } catch (error) {
        this.couponStatus = {
          message: 'Unable to validate coupon',
          type: 'error'
        }
      }
    },

    validateForm() {
      // Clear any existing status messages
      this.statusMessage = null

      if (!this.signupForm.fullName) {
        this.showStatusMessage('error', 'Validation Error', 'Please enter your full name.')
        return false
      }

      if (!this.signupForm.email) {
        this.showStatusMessage('error', 'Validation Error', 'Please enter your email address.')
        return false
      }

      if (!this.isValidEmail(this.signupForm.email)) {
        this.showStatusMessage('error', 'Validation Error', 'Please enter a valid email address.')
        return false
      }

      if (!this.signupForm.password) {
        this.showStatusMessage('error', 'Validation Error', 'Please enter a password.')
        return false
      }

      if (this.signupForm.password.length < 6) {
        this.showStatusMessage('error', 'Validation Error', 'Password must be at least 6 characters long.')
        return false
      }

      return true
    },

    async handleSubscribe() {
      if (!this.validateForm()) {
        return
      }

      if (!this.stripe || !this.cardElement) {
        this.showStatusMessage('error', 'Payment Error', 'Payment system not ready. Please refresh and try again.')
        return
      }

      this.loading = true
      this.statusMessage = null

      try {
        // Step 1: Create Stripe Payment Method
        const { error: stripeError, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.signupForm.fullName,
            email: this.signupForm.email
          }
        })
        
        if (stripeError) {
          throw new Error(stripeError.message)
        }
        
        // Step 2: Create subscription and account (backend handles both)
        const subscriptionResponse = await this.createSubscription(paymentMethod.id)
        
        if (!subscriptionResponse.success) {
          throw new Error(subscriptionResponse.error || 'Failed to create subscription')
        }
        
        // Step 3: Success - account is already created by backend
        this.showStatusMessage('success', 'Welcome!', 'Account created successfully! You can now log in with your credentials.')
        
        setTimeout(() => {
          this.$emit('close')
          // Emit signup success with login credentials
          this.$emit('signup-success', {
            email: this.signupForm.email,
            password: this.signupForm.password,
            fullName: this.signupForm.fullName,
            canAutoLogin: true
          })
        }, 2000)
        
      } catch (error) {
        console.error('Signup error:', error)
        this.showStatusMessage('error', 'Signup Failed', error.message || 'Failed to create account. Please try again.')
      } finally {
        this.loading = false
      }
    },
    
    async createSubscription(paymentMethodId) {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://interview-signal-api-production.up.railway.app'
        : 'http://localhost:3001'
      
      const requestBody = {
        email: this.signupForm.email,
        full_name: this.signupForm.fullName,
        payment_method_id: paymentMethodId,
        password: this.signupForm.password
      }
      
      // Add coupon if provided
      if (this.signupForm.couponCode.trim()) {
        requestBody.coupon_code = this.signupForm.couponCode.trim()
      }
      
      const response = await fetch(`${apiUrl}/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
      
      return await response.json()
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    showStatusMessage(type, title, text) {
      this.statusMessage = { type, title, text }
      setTimeout(() => {
        this.statusMessage = null
      }, 5000)
    }
  }
}
</script>

<style scoped>
.auth-form {
  padding: 0;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
  font-size: 14px;
}

.auth-input {
  width: 100%;
}

.subscribe-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  margin-top: 12px;
}

.subscription-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
  text-align: center;
}

.plan-details h4 {
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.pricing-offer {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.price-amount {
  font-size: 18px;
  color: #2d3748;
  font-weight: 700;
  display: block;
  margin-bottom: 6px;
}

.feature-list {
  font-size: 12px;
  color: #4a5568;
  font-weight: 500;
  line-height: 1.3;
}

.compact-guarantee {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 12px 0;
  padding: 6px;
  background: #f0f7ff;
  border: 1px solid #409eff;
  border-radius: 6px;
  color: #409eff;
  font-size: 14px;
  font-weight: 500;
}

.compact-guarantee i {
  color: #409eff;
  font-size: 16px;
}

.status-message {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.status-message.success {
  background-color: #f0fff4;
  border: 1px solid #9ae6b4;
  color: #276749;
}

.status-message.error {
  background-color: #fed7d7;
  border: 1px solid #feb2b2;
  color: #c53030;
}

.status-message i {
  margin-right: 10px;
  margin-top: 2px;
}

.message-content {
  flex: 1;
}

.message-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.message-text {
  font-size: 14px;
}

.terms-agreement {
  margin-top: 12px;
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
}

.terms-agreement p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.terms-link {
  color: #0a66c2;
  text-decoration: none;
  font-weight: 500;
}

.terms-link:hover {
  color: #004182;
  text-decoration: underline;
}

/* Remove gray header bar completely */
::v-deep .el-dialog__header {
  display: none;
}

/* Ensure dialog body starts at the top */
::v-deep .el-dialog__body {
  padding: 20px 20px 20px 20px;
  max-height: calc(90vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Style the close button */
::v-deep .el-dialog__close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 18px;
  color: #909399;
  cursor: pointer;
  z-index: 10;
}

::v-deep .el-dialog__close:hover {
  color: #606266;
}

/* Ensure the dialog wrapper has proper styling */
::v-deep .el-dialog {
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: none !important;
}

/* Prevent body scroll when modal is open */
::v-deep .el-dialog__wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Ensure modal overlay covers entire screen */
::v-deep .v-modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
}

/* Payment section styles */
.payment-section {
  margin: 20px 0;
}

.payment-section label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.payment-note {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  text-align: center;
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 4px;
}

.stripe-element {
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: white;
  transition: border-color 0.3s;
}

.stripe-element:focus-within {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* Compact form styling */
.form-group.compact {
  margin-bottom: 12px;
}

.form-group.compact .auth-input {
  font-size: 14px;
}

.section-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
  font-size: 14px;
}

.payment-section.compact {
  margin-bottom: 12px;
}

.stripe-element.compact {
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  min-height: 44px;
  background: white;
  box-sizing: border-box;
  transition: border-color 0.3s;
}

.stripe-element.compact:focus-within {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* Ensure Stripe iframe has proper styling */
.stripe-element.compact iframe {
  height: 20px !important;
  width: 100% !important;
}

.stripe-loading {
  color: #999;
  font-style: italic;
  padding: 2px 0;
  font-size: 14px;
}

.coupon-section {
  margin-bottom: 12px;
  margin-top: 8px;
}

.coupon-toggle {
  margin-bottom: 6px;
}

.coupon-toggle .el-checkbox {
  display: flex;
  align-items: center;
}

.coupon-input {
  margin-left: 24px;
  margin-top: 4px;
}

.coupon-field {
  max-width: 200px;
}

.coupon-status {
  font-size: 12px;
  margin-top: 4px;
  padding: 2px 0;
}

.coupon-status.success {
  color: #67c23a;
}

.coupon-status.error {
  color: #f56c6c;
}

.compact-guarantee {
  display: flex;
  align-items: center;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #0369a1;
}

.compact-guarantee i {
  color: #0ea5e9;
  margin-right: 6px;
}

.terms-agreement {
  margin-bottom: 16px;
}

.terms-agreement p {
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
  margin: 0;
  text-align: center;
}

.terms-link {
  color: #409eff;
  text-decoration: none;
}

.terms-link:hover {
  text-decoration: underline;
}

.subscribe-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
}

.stripe-errors {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
  min-height: 14px;
  line-height: 1.2;
}

.field-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: block;
}

/* Ensure modal overlay covers entire screen */
::v-deep .v-modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
</style>
